import { connectorBounds, type NodeLookup } from './connector.js';
import {
  boundsOfRects,
  rectCenter,
  rectNormalize,
  rotatedBounds,
  vRotate,
} from './geometry.js';
import type { BoardNode, Rect, Vec } from './types.js';

/** Un-rotated box of a node, in board coordinates. */
export function nodeBox(node: BoardNode): Rect {
  return { x: node.x, y: node.y, w: node.w, h: node.h };
}

/**
 * Axis-aligned bounds actually occupied on screen: rotation applied, and
 * connectors resolved through their attached nodes.
 */
export function nodeAABB(node: BoardNode, lookup: NodeLookup): Rect {
  if (node.type === 'connector') return connectorBounds(node, lookup);
  return rotatedBounds(nodeBox(node), node.rotation);
}

export function selectionBounds(nodes: readonly BoardNode[], lookup: NodeLookup): Rect {
  if (nodes.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  if (nodes.length === 1) {
    const only = nodes[0]!;
    // A single node keeps its own rotated frame, so handles line up with it.
    return only.type === 'connector' ? connectorBounds(only, lookup) : nodeBox(only);
  }
  return boundsOfRects(nodes.map((n) => nodeAABB(n, lookup)));
}

/** True when the selection can show rotated handles rather than an AABB. */
export function selectionRotation(nodes: readonly BoardNode[]): number {
  if (nodes.length !== 1) return 0;
  const only = nodes[0]!;
  return only.type === 'connector' ? 0 : only.rotation;
}

// ---------------------------------------------------------------------------
// Resize
// ---------------------------------------------------------------------------

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const RESIZE_HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const HANDLE_ORIGIN: Record<ResizeHandle, Vec> = {
  nw: { x: 1, y: 1 },
  n: { x: 0.5, y: 1 },
  ne: { x: 0, y: 1 },
  e: { x: 0, y: 0.5 },
  se: { x: 0, y: 0 },
  s: { x: 0.5, y: 0 },
  sw: { x: 1, y: 0 },
  w: { x: 1, y: 0.5 },
};

export function handlePoint(bounds: Rect, handle: ResizeHandle, rotation = 0): Vec {
  const nx = 1 - HANDLE_ORIGIN[handle].x;
  const ny = 1 - HANDLE_ORIGIN[handle].y;
  const p = { x: bounds.x + bounds.w * nx, y: bounds.y + bounds.h * ny };
  return rotation === 0 ? p : vRotate(p, rectCenter(bounds), rotation);
}

export interface ResizeOptions {
  /** Shift: keep the original aspect ratio. */
  lockAspect?: boolean;
  /** Alt: resize symmetrically about the centre. */
  fromCenter?: boolean;
  /** Minimum edge length; smaller drags clamp instead of inverting. */
  min?: number;
}

/**
 * New bounds for a resize gesture.
 *
 * Works in the selection's own rotated frame: the pointer is un-rotated first,
 * the box is edited axis-aligned, and the caller re-applies the rotation. That
 * keeps rotated resizing intuitive (dragging the "east" handle of a 30-degree
 * box widens it along its own axis, not the screen axis).
 */
export function resizeBounds(
  start: Rect,
  handle: ResizeHandle,
  pointerLocal: Vec,
  options: ResizeOptions = {},
): Rect {
  const min = options.min ?? 1;
  const originN = HANDLE_ORIGIN[handle];
  const anchor: Vec = {
    x: start.x + start.w * originN.x,
    y: start.y + start.h * originN.y,
  };

  const movesX = handle !== 'n' && handle !== 's';
  const movesY = handle !== 'e' && handle !== 'w';

  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;

  if (movesX) {
    if (originN.x === 1) left = pointerLocal.x;
    else if (originN.x === 0) right = pointerLocal.x;
  }
  if (movesY) {
    if (originN.y === 1) top = pointerLocal.y;
    else if (originN.y === 0) bottom = pointerLocal.y;
  }

  let next = rectNormalize({ x: left, y: top, w: right - left, h: bottom - top });

  if (options.lockAspect && start.w > 0 && start.h > 0) {
    const ratio = start.w / start.h;
    if (movesX && movesY) {
      // Corner: fit the larger axis and derive the other.
      if (next.w / next.h > ratio) next.h = next.w / ratio;
      else next.w = next.h * ratio;
    } else if (movesX) {
      next.h = next.w / ratio;
    } else {
      next.w = next.h * ratio;
    }
    // Re-anchor after the aspect correction.
    next.x = originN.x === 1 ? anchor.x - next.w : originN.x === 0 ? anchor.x : anchor.x - next.w / 2;
    next.y = originN.y === 1 ? anchor.y - next.h : originN.y === 0 ? anchor.y : anchor.y - next.h / 2;
  }

  if (options.fromCenter) {
    const c = rectCenter(start);
    const halfW = Math.max(Math.abs(pointerLocal.x - c.x), min / 2);
    const halfH = Math.max(Math.abs(pointerLocal.y - c.y), min / 2);
    next = {
      x: movesX ? c.x - halfW : next.x,
      y: movesY ? c.y - halfH : next.y,
      w: movesX ? halfW * 2 : next.w,
      h: movesY ? halfH * 2 : next.h,
    };
    if (options.lockAspect && start.w > 0 && start.h > 0) {
      const ratio = start.w / start.h;
      if (next.w / next.h > ratio) next.h = next.w / ratio;
      else next.w = next.h * ratio;
      next.x = c.x - next.w / 2;
      next.y = c.y - next.h / 2;
    }
  }

  if (next.w < min) {
    next.w = min;
    if (originN.x === 1) next.x = anchor.x - min;
    else if (originN.x === 0) next.x = anchor.x;
  }
  if (next.h < min) {
    next.h = min;
    if (originN.y === 1) next.y = anchor.y - min;
    else if (originN.y === 0) next.y = anchor.y;
  }

  return next;
}

/**
 * Maps a node from one bounding box into another, preserving its relative
 * position and size. Used for multi-select resize and for scaling a pasted
 * selection into place.
 */
export function remapBox(box: Rect, from: Rect, to: Rect): Rect {
  const sx = from.w === 0 ? 1 : to.w / from.w;
  const sy = from.h === 0 ? 1 : to.h / from.h;
  return {
    x: to.x + (box.x - from.x) * sx,
    y: to.y + (box.y - from.y) * sy,
    w: box.w * sx,
    h: box.h * sy,
  };
}

export function scaleFactors(from: Rect, to: Rect): Vec {
  return { x: from.w === 0 ? 1 : to.w / from.w, y: from.h === 0 ? 1 : to.h / from.h };
}

// ---------------------------------------------------------------------------
// Align and distribute
// ---------------------------------------------------------------------------

export type AlignAxis =
  | 'left'
  | 'center-h'
  | 'right'
  | 'top'
  | 'center-v'
  | 'bottom';

export function alignOffset(bounds: Rect, target: Rect, axis: AlignAxis): Vec {
  switch (axis) {
    case 'left':
      return { x: target.x - bounds.x, y: 0 };
    case 'right':
      return { x: target.x + target.w - (bounds.x + bounds.w), y: 0 };
    case 'center-h':
      return { x: target.x + target.w / 2 - (bounds.x + bounds.w / 2), y: 0 };
    case 'top':
      return { x: 0, y: target.y - bounds.y };
    case 'bottom':
      return { x: 0, y: target.y + target.h - (bounds.y + bounds.h) };
    case 'center-v':
      return { x: 0, y: target.y + target.h / 2 - (bounds.y + bounds.h / 2) };
  }
}

/**
 * Even gaps along one axis. Returns the delta to apply to each input, in the
 * same order as the input array.
 */
export function distributeOffsets(rects: readonly Rect[], axis: 'x' | 'y'): Vec[] {
  if (rects.length < 3) return rects.map(() => ({ x: 0, y: 0 }));
  const order = rects
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (axis === 'x' ? a.r.x - b.r.x : a.r.y - b.r.y));

  const size = (r: Rect) => (axis === 'x' ? r.w : r.h);
  const pos = (r: Rect) => (axis === 'x' ? r.x : r.y);

  const first = order[0]!.r;
  const last = order[order.length - 1]!.r;
  const span = pos(last) + size(last) - pos(first);
  const totalSize = order.reduce((acc, o) => acc + size(o.r), 0);
  const gap = (span - totalSize) / (order.length - 1);

  const out: Vec[] = rects.map(() => ({ x: 0, y: 0 }));
  let cursor = pos(first);
  for (const o of order) {
    const delta = cursor - pos(o.r);
    out[o.i] = axis === 'x' ? { x: delta, y: 0 } : { x: 0, y: delta };
    cursor += size(o.r) + gap;
  }
  return out;
}

/** Pack items into a grid, the "tidy up" command. */
export function tidyGridOffsets(
  rects: readonly Rect[],
  gap = 24,
  columns?: number,
): Vec[] {
  if (rects.length === 0) return [];
  const cols = columns ?? Math.max(1, Math.round(Math.sqrt(rects.length)));
  const order = rects
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.y - b.r.y || a.r.x - b.r.x);

  const colWidth = Math.max(...rects.map((r) => r.w));
  const rowHeight = Math.max(...rects.map((r) => r.h));
  const origin = boundsOfRects(rects as Rect[]);

  const out: Vec[] = rects.map(() => ({ x: 0, y: 0 }));
  order.forEach((o, k) => {
    const col = k % cols;
    const row = Math.floor(k / cols);
    const x = origin.x + col * (colWidth + gap);
    const y = origin.y + row * (rowHeight + gap);
    out[o.i] = { x: x - o.r.x, y: y - o.r.y };
  });
  return out;
}
